import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AddToCartMutationVariables, SCartDto } from "@/__generated__/sdk";
import { SessionState } from "http2";
import sessionSlice from "../session/sessionSlice";
import { RootState } from "@/redux/store";
import { GqlClient } from "@/Providers/gqlClients/GqlClient";
import getError from "@/lib/getError";

export const addOrUpdateItemInCart = createAsyncThunk(
  "cart/addOrUpdateItemInCart",
  async (variables: AddToCartMutationVariables, thunkAPI) => {
    try {
      const { session } = (thunkAPI.getState() as RootState).session;
      const { gqlClient } = new GqlClient(session);
      const response = await gqlClient.AddToCart(variables);
      return response?.addToCart as SCartDto;
    } catch (error: any) {
      // Pass the error message or a default one if not available
      return thunkAPI.rejectWithValue(getError(error) || "خطایی رخ داد");
    }
  },
);

export const getCart = createAsyncThunk("cart/getCart", async (_, thunkAPI) => {
  try {
    const { session } = (thunkAPI.getState() as RootState).session;
    const { gqlClient } = new GqlClient(session);
    const response = await gqlClient.GetCart();
    return response?.getCart as SCartDto;
  } catch (error: any) {
    // Pass the error message or a default one if not available
    return thunkAPI.rejectWithValue(getError(error) || "خطایی رخ داد");
  }
});
interface CartState {
  cart: SCartDto | null;
  itemsCount: number | null;
  loading: boolean;
  error: string | null; // Add error field to the state
}

const initialState = { cart: null, itemsCount: null, loading: false, error: null } satisfies CartState as CartState;

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    updateCart(state, action: PayloadAction<SCartDto | null>) {
      state.cart = action.payload;
      state.itemsCount =
        action.payload && action.payload.cartItems.length > 0 ? action.payload.cartItems.reduce((c, item) => c + item.quantity, 0) : null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addOrUpdateItemInCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOrUpdateItemInCart.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.itemsCount =
          action.payload && action.payload.cartItems.length > 0 ? action.payload.cartItems.reduce((c, item) => c + item.quantity, 0) : null;
        state.error = null;
        state.loading = false;
      })
      .addCase(addOrUpdateItemInCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        setTimeout(() => {
          state.error = null;
        }, 1000);
      });
    builder
      .addCase(getCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCart.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.itemsCount =
          action.payload && action.payload.cartItems.length > 0 ? action.payload.cartItems.reduce((c, item) => c + item.quantity, 0) : null;
        state.error = null;
        state.loading = false;
      })
      .addCase(getCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        setTimeout(() => {
          state.error = null;
        }, 1000);
      });
  },
});

export const { updateCart } = cartSlice.actions;
export default cartSlice.reducer;
